'use client';

import { faCloudArrowUp, faMobileScreen } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DropPlaceholder } from '@form/FormDropzone';
import { MAX_UPLOAD_BYTES } from '@form/FormDropzone/constants';
import { Button } from '@ui';
import { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { cn } from '~/lib/cn';

// react-dropzone v11 takes MIME types as a string list, not the v12 object map.
const ACCEPT = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];

interface Props {
    onFiles: (files: File[]) => void;
    /** Omitted where there is no session to pair a phone with. */
    onScanRequest?: () => void;
    isDisabled?: boolean;
    className?: string;
    /** Grows into the space the rail has left over — for the assistant's rail. */
    fill?: boolean;
}

/**
 * Drops receipts into a capture session.
 *
 * Hands the caller raw File objects rather than uploaded ids: each file goes to the capture
 * endpoint that also queues its extraction, which is not what FormDropzone's upload-on-mount
 * flow does.
 */
const BelegDropzone = ({ onFiles, onScanRequest, isDisabled, className, fill }: Props) => {
    const { t } = useTranslation('invoice');

    const onDrop = useCallback(
        (accepted: File[]) => {
            if (accepted.length > 0) onFiles(accepted);
        },
        [onFiles]
    );

    const { getRootProps, getInputProps, isDragActive, isDragReject, open } = useDropzone({
        onDrop,
        accept: ACCEPT,
        // One request per file, and API Gateway caps a Lambda call at ~6MB.
        maxSize: MAX_UPLOAD_BYTES,
        multiple: true,
        noClick: true,
        noKeyboard: true,
        disabled: isDisabled
    });

    return (
        <div className={cn('tw-flex tw-flex-col tw-gap-3', fill && 'tw-min-h-0 tw-flex-1', className)}>
            <div
                {...getRootProps()}
                className={cn(
                    'tw-cursor-pointer',
                    fill && 'tw-min-h-0 tw-flex-1',
                    isDisabled && 'tw-pointer-events-none tw-opacity-60'
                )}
                onClick={isDisabled ? undefined : open}
            >
                {/* useDropzone's open() drives this input, so it has to stay mounted. */}
                <input {...getInputProps()} />
                <DropPlaceholder
                    name="beleg_files"
                    isDragActive={isDragActive}
                    isDragReject={isDragReject}
                    className={cn(fill && 'tw-flex tw-h-full tw-items-center tw-justify-center')}
                />
            </div>

            {/* Side by side outside the rail, where there is width for it. */}
            <div className={cn('tw-flex tw-shrink-0 tw-gap-2', fill ? 'tw-flex-col' : 'tw-flex-row')}>
                <div className={cn(!fill && 'tw-flex-1')}>
                    <Button
                        block
                        size="sm"
                        color="success"
                        isDisabled={isDisabled}
                        onPress={open}
                        icon={<FontAwesomeIcon icon={faCloudArrowUp} />}
                    >
                        {t('belegAssistant.dropzone.select')}
                    </Button>
                </div>

                {onScanRequest && (
                    <div className={cn(!fill && 'tw-flex-1')}>
                        <Button
                            block
                            size="sm"
                            color="success"
                            flat
                            isDisabled={isDisabled}
                            onPress={onScanRequest}
                            icon={<FontAwesomeIcon icon={faMobileScreen} />}
                        >
                            {t('belegAssistant.dropzone.scanWithPhone')}
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BelegDropzone;
